import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload,
  Download,
  CheckCircle,
  Error,
  Info,
  Delete,
} from '@mui/icons-material';
import { useTradingJournal } from '../../contexts/TradingJournalContext';
import { Trade } from '../../contexts/TradingJournalContext';

interface TradeImportProps {
  open: boolean;
  onClose: () => void;
}

interface ImportResult {
  success: boolean;
  message: string;
  rowNumber?: number;
  field?: string;
}

interface ParsedTrade extends Omit<Trade, 'id' | 'createdAt' | 'updatedAt'> {
  rowNumber: number;
}

const TradeImport: React.FC<TradeImportProps> = ({ open, onClose }) => {
  const { addTrade } = useTradingJournal();
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [parsedTrades, setParsedTrades] = useState<ParsedTrade[]>([]);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const steps = ['Download Template', 'Upload File', 'Review Data', 'Import Results'];

  // Generate CSV template
  const generateTemplate = () => {
    const headers = [
      'broker',
      'symbol',
      'type',
      'entryPrice',
      'exitPrice',
      'quantity',
      'entryTime',
      'exitTime',
      'stopLoss',
      'takeProfit',
      'commission',
      'swap',
      'status',
      'strategy',
      'notes',
      'tags',
      'confidence',
      'emotion',
      'marketCondition',
      'timeframe',
      'setup'
    ];

    const sampleRows = [
      [
        'Weltrade',
        'EURUSD',
        'buy',
        '1.0850',
        '1.0920',
        '0.1',
        '2024-01-15 09:30:00',
        '2024-01-15 14:15:00',
        '1.0800',
        '1.0950',
        '0.50',
        '0.00',
        'closed',
        'Breakout',
        'Clean breakout above resistance',
        'breakout,morning,eur',
        '4',
        'confident',
        'trending',
        'H1',
        'Support/Resistance Break'
      ],
      [
        'Deriv',
        'BOOM1000',
        'sell',
        '850.25',
        '845.80',
        '0.05',
        '2024-01-16 11:00:00',
        '2024-01-16 11:45:00',
        '855.00',
        '840.00',
        '0.25',
        '0.00',
        'closed',
        'Mean Reversion',
        'Overbought signal on RSI',
        'synthetic,mean-reversion',
        '3',
        'neutral',
        'volatile',
        'M15',
        'RSI Divergence'
      ],
      [
        'XM',
        'XAUUSD',
        'buy',
        '2025.50',
        '',
        '0.01',
        '2024-01-17 13:20:00',
        '',
        '2015.00',
        '2045.00',
        '0.30',
        '0.00',
        'open',
        'Trend Following',
        'Following uptrend after pullback',
        'gold,uptrend,pullback',
        '5',
        'disciplined',
        'trending',
        'H4',
        'Trend Continuation'
      ]
    ];

    const csvContent = [
      headers.join(','),
      ...sampleRows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'trading_journal_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setActiveStep(1);
  };

  // Parse CSV file
  const parseCSV = (csvText: string): ParsedTrade[] => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
    const trades: ParsedTrade[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length === 0) continue;

      const trade: any = { rowNumber: i + 1 };

      headers.forEach((header, index) => {
        const value = values[index]?.replace(/"/g, '').trim();
        if (!value) return;

        switch (header) {
          case 'broker':
            // Accept any broker name, no longer restricted to specific values
            trade.broker = value;
            break;
          case 'symbol':
            trade.symbol = value;
            break;
          case 'type':
            if (['buy', 'sell'].includes(value.toLowerCase())) {
              trade.type = value.toLowerCase();
            }
            break;
          case 'entryPrice':
          case 'exitPrice':
          case 'quantity':
          case 'stopLoss':
          case 'takeProfit':
          case 'commission':
          case 'swap':
          case 'confidence':
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
              trade[header] = numValue;
            }
            break;
          case 'entryTime':
          case 'exitTime':
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              trade[header] = date;
            }
            break;
          case 'status':
            if (['open', 'closed', 'cancelled'].includes(value.toLowerCase())) {
              trade.status = value.toLowerCase();
            }
            break;
          case 'emotion':
            if (['confident', 'fearful', 'greedy', 'neutral', 'disciplined'].includes(value.toLowerCase())) {
              trade.emotion = value.toLowerCase();
            }
            break;
          case 'marketCondition':
            if (['trending', 'ranging', 'volatile', 'calm'].includes(value.toLowerCase())) {
              trade.marketCondition = value.toLowerCase();
            }
            break;
          case 'tags':
            trade.tags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
            break;
          default:
            trade[header] = value;
        }
      });

      trades.push(trade);
    }

    return trades;
  };

  // Parse CSV line handling commas within quotes
  const parseCSVLine = (line: string): string[] => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current);
    return result;
  };

  // Validate trade data
  const validateTrade = (trade: ParsedTrade): string[] => {
    const errors: string[] = [];

    if (!trade.broker) errors.push('Broker is required');
    if (!trade.symbol) errors.push('Symbol is required');
    if (!trade.type) errors.push('Type is required');
    if (!trade.entryPrice) errors.push('Entry price is required');
    if (!trade.quantity) errors.push('Quantity is required');
    if (!trade.entryTime) errors.push('Entry time is required');
    if (trade.commission === undefined) errors.push('Commission is required');

    if (trade.confidence && (trade.confidence < 1 || trade.confidence > 5)) {
      errors.push('Confidence must be between 1-5');
    }

    if (trade.exitPrice && trade.exitPrice <= 0) {
      errors.push('Exit price must be positive');
    }

    if (trade.stopLoss && trade.takeProfit && trade.stopLoss === trade.takeProfit) {
      errors.push('Stop loss and take profit cannot be the same');
    }

    return errors;
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    if (!uploadedFile.name.toLowerCase().endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }

    setFile(uploadedFile);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseCSV(text);
      setParsedTrades(parsed);
      setActiveStep(2);
    };
    reader.readAsText(uploadedFile);
  };

  // Import trades
  const handleImport = async () => {
    setIsImporting(true);
    setImportProgress(0);
    const results: ImportResult[] = [];

    for (let i = 0; i < parsedTrades.length; i++) {
      const trade = parsedTrades[i];
      const errors = validateTrade(trade);

      if (errors.length > 0) {
        results.push({
          success: false,
          message: errors.join(', '),
          rowNumber: trade.rowNumber,
        });
      } else {
        try {
          // Remove rowNumber before adding to database
          const { rowNumber, ...tradeData } = trade;
          await addTrade(tradeData);
          results.push({
            success: true,
            message: 'Successfully imported',
            rowNumber: trade.rowNumber,
          });
        } catch (error) {
          results.push({
            success: false,
            message: `Failed to import: ${error}`,
            rowNumber: trade.rowNumber,
          });
        }
      }

      setImportProgress(((i + 1) / parsedTrades.length) * 100);
    }

    setImportResults(results);
    setIsImporting(false);
    setActiveStep(3);
  };

  // Remove trade from preview
  const removeTrade = (index: number) => {
    const newTrades = [...parsedTrades];
    newTrades.splice(index, 1);
    setParsedTrades(newTrades);
  };

  // Reset and close
  const handleClose = () => {
    setActiveStep(0);
    setFile(null);
    setParsedTrades([]);
    setImportResults([]);
    setImportProgress(0);
    setIsImporting(false);
    onClose();
  };

  // Get validation errors for a trade
  const getTradeErrors = (trade: ParsedTrade) => validateTrade(trade);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
        Import Trades
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 0: Download Template */}
        {activeStep === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Download Template
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Download the CSV template, fill it with your trade data, and upload it back.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={generateTemplate}
              size="large"
            >
              Download CSV Template
            </Button>
            <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
              <Typography variant="subtitle2" gutterBottom>Template includes:</Typography>
              <ul>
                <li>Required fields: broker, symbol, type, entryPrice, quantity, entryTime, commission</li>
                <li>Optional fields: exitPrice, exitTime, stopLoss, takeProfit, strategy, notes, etc.</li>
                <li>Sample data to guide your formatting</li>
                <li>Popular brokers include: Weltrade, Deriv, XM, IG Markets, OANDA, Interactive Brokers, etc.</li>
                <li>You can use any broker name - not limited to predefined options</li>
              </ul>
            </Alert>
          </Box>
        )}

        {/* Step 1: Upload File */}
        {activeStep === 1 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Upload CSV File
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Select your completed CSV file to upload and review.
            </Typography>
            
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => fileInputRef.current?.click()}
              size="large"
              sx={{ mb: 2 }}
            >
              Choose CSV File
            </Button>
            
            {file && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        )}

        {/* Step 2: Review Data */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Parsed Data ({parsedTrades.length} trades)
            </Typography>
            
            {parsedTrades.length === 0 ? (
              <Alert severity="warning">
                No valid trades found in the CSV file. Please check your file format.
              </Alert>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Broker</TableCell>
                      <TableCell>Symbol</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Entry Price</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Validation</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedTrades.map((trade, index) => {
                      const errors = getTradeErrors(trade);
                      return (
                        <TableRow key={index}>
                          <TableCell>{trade.rowNumber}</TableCell>
                          <TableCell>{trade.broker || '-'}</TableCell>
                          <TableCell>{trade.symbol || '-'}</TableCell>
                          <TableCell>{trade.type || '-'}</TableCell>
                          <TableCell>{trade.entryPrice || '-'}</TableCell>
                          <TableCell>{trade.quantity || '-'}</TableCell>
                          <TableCell>{trade.status || '-'}</TableCell>
                          <TableCell>
                            {errors.length === 0 ? (
                              <Chip icon={<CheckCircle />} label="Valid" color="success" size="small" />
                            ) : (
                              <Tooltip title={errors.join(', ')}>
                                <Chip icon={<Error />} label={`${errors.length} errors`} color="error" size="small" />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton onClick={() => removeTrade(index)} size="small">
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Step 3: Import Results */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Import Results
            </Typography>
            
            {isImporting && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Importing trades... {Math.round(importProgress)}%
                </Typography>
                <LinearProgress variant="determinate" value={importProgress} />
              </Box>
            )}

            {importResults.length > 0 && (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importResults.map((result, index) => (
                      <TableRow key={index}>
                        <TableCell>{result.rowNumber}</TableCell>
                        <TableCell>
                          <Chip
                            icon={result.success ? <CheckCircle /> : <Error />}
                            label={result.success ? 'Success' : 'Failed'}
                            color={result.success ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{result.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {importResults.length > 0 && !isImporting && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Import completed: {importResults.filter(r => r.success).length} successful, {importResults.filter(r => !r.success).length} failed
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={handleClose}>
          {activeStep === 3 ? 'Close' : 'Cancel'}
        </Button>
        
        {activeStep === 1 && (
          <Button onClick={() => setActiveStep(0)} variant="outlined">
            Back
          </Button>
        )}
        
        {activeStep === 2 && (
          <>
            <Button onClick={() => setActiveStep(1)} variant="outlined">
              Back
            </Button>
            <Button 
              onClick={handleImport} 
              variant="contained"
              disabled={parsedTrades.length === 0 || isImporting}
            >
              Import {parsedTrades.length} Trades
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TradeImport; 